import { map, flatMap, isEqual, isEmpty } from 'lodash';
import { getNodeId, getNodeParentId } from './adapterActions';

const isChangeRequest = m => m.isChange;

const collectTreeChanges = (
  { models = {}, children = { [null]: [] }, parents = {} },
  incomingModelsOrChangesArray,
  { adapter },
  { parentId = undefined } = {},
) => {
  const added = [];
  const changed = [];
  const removedIds = initRemovedIds();
  const inputModels = initInputModels();

  if (inputModels) {
    inputModels.forEach(processIncomingModelOrChange);
  }

  populateRemoveIdsWithChildren();

  return { added, changed, removedIds };

  function processIncomingModelOrChange(modelOrChange) {
    if (isChangeRequest(modelOrChange)) {
      processIncomingChange(modelOrChange);
    } else {
      processIncomingModel(modelOrChange);
    }
  }

  function processIncomingModel(model) {
    const id = getNodeId(adapter, model);
    const oldModel = models[id];

    if (!oldModel) {
      added.push(model);
    } else if (!isEqual(oldModel, model)) {
      const parentId = getNodeParentId(adapter, model);
      const oldParentId = parents[id];

      if ((!parentId && !oldParentId) || parentId === oldParentId) {
        changed.push(model);
      } else {
        added.push(model);
        return;
      }
    }

    removedIds.delete(id);
  }

  function processIncomingChange(change) {
    for (const model of change.added || []) {
      added.push(model);
      removedIds.delete(getNodeId(adapter, model));
    }

    for (const model of change.changed || []) {
      changed.push(model);
      removedIds.delete(getNodeId(adapter, model));
    }
  }

  function initRemovedIds() {
    let removedIds = incomingModelsOrChangesArray
      .filter(m => !isChangeRequest(m))
      .map(m => getNodeId(adapter, m));

    if (parentId === undefined) {
      removedIds = removedIds.concat(map(models, m => getNodeId(adapter, m)));
    } else {
      removedIds = removedIds.concat(children[parentId] || []);
    }

    removedIds = removedIds.concat(
      flatMap(
        incomingModelsOrChangesArray
          .filter(m => isChangeRequest(m))
          .map(m => m.removedIds || []),
      ),
    );

    return new Set(removedIds);
  }

  function initInputModels() {
    return incomingModelsOrChangesArray;
  }

  function populateRemoveIdsWithChildren() {
    let childrenIds = [];

    for (const removeId of Array.from(removedIds)) {
      /** This check is for the Parent folder where removed Parent gets readded back */
      const isRemovedIdPartOfAdded = !!added.find(
        a => getNodeId(adapter, a) === removeId,
      );
      if (!childrenIds.includes(removeId) && !isRemovedIdPartOfAdded) {
        childrenIds = childrenIds.concat(getNodeGeneration(removeId));
      }
    }

    childrenIds.forEach(v => {
      removedIds.add(v);
    });
  }

  function getNodeGeneration(id, generation = []) {
    const nodeChildren = children[id];

    if (isEmpty(nodeChildren)) {
      return generation;
    }
    generation = generation.concat(nodeChildren);

    const childGeneration = nodeChildren.reduce((acc, v) => {
      const generation = getNodeGeneration(v, []);
      if (isEmpty(generation)) {
        return acc;
      }
      return acc.concat(generation);
    }, []);
    generation = generation.concat(childGeneration);
    return generation;
  }
};
export default collectTreeChanges;
